// Analytics — the machine's numbers: sends, replies, funnel, capacity.
// Charts follow the dataviz method: validated palette, thin marks, 2px surface
// gaps, rounded data-ends, legends for multi-series, tooltips, table view.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, MessageSquare, Percent, Database, Layers, RefreshCw } from 'lucide-react';
import { apiService } from '../services/apiService';
import { Card, StatCard, Btn, Spinner, fmtNum } from './ui';

const VIZ_CSS = `
.viz-root{--s1:#2a78d6;--s2:#eb6834;--s3:#1baf7a;}
.dark .viz-root{--s1:#3987e5;--s2:#d95926;--s3:#199e70;}
`;

const CHART_H = 190;
const PAD = { l: 44, r: 8, t: 12, b: 22 };

const niceTicks = (max) => {
  if (max <= 0) return [0, 1];
  const step = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000]
    .find((s) => max / s <= 5) || Math.ceil(max / 5000) * 1000;
  const ticks = [];
  for (let v = 0; v <= max + step - 1; v += step) ticks.push(v);
  return ticks;
};

const useTooltip = () => {
  const [tip, setTip] = useState(null); // {x, y, lines}
  const show = (e, lines) => {
    const host = e.currentTarget.closest('[data-tiphost]').getBoundingClientRect();
    setTip({ x: e.clientX - host.left, y: e.clientY - host.top, lines });
  };
  const node = tip && (
    <div style={{
      position: 'absolute', left: Math.min(tip.x + 12, 640), top: Math.max(tip.y - 10, 0),
      background: 'var(--c-bg)', border: '1px solid var(--c-border)', borderRadius: 8,
      padding: '7px 10px', fontSize: 11.5, color: 'var(--c-text)', pointerEvents: 'none',
      boxShadow: 'var(--shadow-elevated)', zIndex: 50, whiteSpace: 'nowrap',
    }}>
      {tip.lines.map((l, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {l.swatch && <span style={{ width: 8, height: 8, borderRadius: 2, background: l.swatch, display: 'inline-block' }} />}
          <span style={{ color: i === 0 ? 'var(--c-text)' : 'var(--c-text-2)', fontWeight: i === 0 ? 700 : 500 }}>{l.text}</span>
        </div>
      ))}
    </div>
  );
  return [show, () => setTip(null), node];
};

const LegendChip = ({ color, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--c-text-2)', marginRight: 14 }}>
    <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />
    {label}
  </span>
);

/* Stacked daily sends: campaign (s1) + follow-ups (s2). */
const SendsChart = ({ series, width }) => {
  const [show, hide, tipNode] = useTooltip();
  const W = width, H = CHART_H;
  const innerW = W - PAD.l - PAD.r, innerH = H - PAD.t - PAD.b;
  const max = Math.max(...series.map((d) => d.campaign + d.followups), 1);
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1];
  const band = innerW / series.length;
  const bw = Math.min(24, Math.max(4, band - 4));
  const y = (v) => PAD.t + innerH - (v / top) * innerH;
  return (
    <div data-tiphost style={{ position: 'relative' }}>
      <svg width={W} height={H} role="img" aria-label="Emails sent per day">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} stroke="var(--c-border)" strokeWidth="1" />
            <text x={PAD.l - 6} y={y(t) + 3.5} textAnchor="end" fontSize="10" fill="var(--c-text-3)">{fmtNum(t)}</text>
          </g>
        ))}
        {series.map((d, i) => {
          const x = PAD.l + i * band + (band - bw) / 2;
          const hC = (d.campaign / top) * innerH;
          const hF = (d.followups / top) * innerH;
          const yC = PAD.t + innerH - hC;
          const yF = yC - 2 - hF; // 2px surface gap between segments
          const rTop = hF > 0 ? yF : yC;
          return (
            <g key={d.date}>
              {/* campaign segment: rounded top only when it IS the top (no follow-ups above) */}
              {hC > 0 && hF > 0 && <rect x={x} y={yC} width={bw} height={hC} fill="var(--s1)" />}
              {hC > 0 && hF === 0 && <>
                <rect x={x} y={yC} width={bw} height={hC} fill="var(--s1)" rx="4" />
                <rect x={x} y={yC + Math.min(4, hC)} width={bw} height={Math.max(hC - 4, 0)} fill="var(--s1)" />
              </>}
              {/* follow-ups segment: always the top -> rounded data-end, square base */}
              {hF > 0 && <>
                <rect x={x} y={yF} width={bw} height={hF} fill="var(--s2)" rx="4" />
                <rect x={x} y={yF + Math.min(4, hF)} width={bw} height={Math.max(hF - 4, 0)} fill="var(--s2)" />
              </>}
              <rect x={PAD.l + i * band} y={PAD.t} width={band} height={innerH} fill="transparent"
                onMouseMove={(e) => show(e, [
                  { text: new Date(d.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) },
                  { swatch: 'var(--s1)', text: `campaign  ${d.campaign}` },
                  { swatch: 'var(--s2)', text: `follow-ups  ${d.followups}` },
                  { text: `total  ${d.campaign + d.followups}` },
                ])}
                onMouseLeave={hide} />
              {(i === 0 || i === series.length - 1 || new Date(d.date).getDate() === 1) && (
                <text x={PAD.l + i * band + band / 2} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--c-text-3)">
                  {new Date(d.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {tipNode}
    </div>
  );
};

/* Replies per day: single series (s3). Aqua sits under 3:1 on the light
   surface, so the relief rule applies: peak + latest carry visible labels
   and a full table view exists below. */
const RepliesChart = ({ series, width }) => {
  const [show, hide, tipNode] = useTooltip();
  const W = width, H = CHART_H;
  const innerW = W - PAD.l - PAD.r, innerH = H - PAD.t - PAD.b;
  const max = Math.max(...series.map((d) => d.replies), 1);
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1];
  const band = innerW / series.length;
  const bw = Math.min(24, Math.max(4, band - 4));
  const y = (v) => PAD.t + innerH - (v / top) * innerH;
  const peakIdx = series.reduce((bi, d, i) => (d.replies > series[bi].replies ? i : bi), 0);
  return (
    <div data-tiphost style={{ position: 'relative' }}>
      <svg width={W} height={H} role="img" aria-label="Replies per day">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} stroke="var(--c-border)" strokeWidth="1" />
            <text x={PAD.l - 6} y={y(t) + 3.5} textAnchor="end" fontSize="10" fill="var(--c-text-3)">{t}</text>
          </g>
        ))}
        {series.map((d, i) => {
          const x = PAD.l + i * band + (band - bw) / 2;
          const h = (d.replies / top) * innerH;
          const yy = y(d.replies);
          const labeled = d.replies > 0 && (i === peakIdx || i === series.length - 1);
          return (
            <g key={d.date}>
              {h > 0 && <>
                <rect x={x} y={yy} width={bw} height={h} fill="var(--s3)" rx="4" />
                <rect x={x} y={yy + 4} width={bw} height={Math.max(h - 4, 0)} fill="var(--s3)" />
              </>}
              {labeled && (
                <text x={x + bw / 2} y={yy - 4} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="var(--c-text-2)">
                  {d.replies}
                </text>
              )}
              <rect x={PAD.l + i * band} y={PAD.t} width={band} height={innerH} fill="transparent"
                onMouseMove={(e) => show(e, [
                  { text: new Date(d.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) },
                  { swatch: 'var(--s3)', text: `replies  ${d.replies}` },
                ])}
                onMouseLeave={hide} />
              {(i === 0 || i === series.length - 1 || new Date(d.date).getDate() === 1) && (
                <text x={PAD.l + i * band + band / 2} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--c-text-3)">
                  {new Date(d.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {tipNode}
    </div>
  );
};

/* Horizontal bar list: single series, value at the tip. */
const BarList = ({ rows, color = 'var(--s1)', maxWidth = 420 }) => {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div>
      {rows.map((r) => {
        const w = Math.max((r.value / max) * maxWidth, r.value > 0 ? 6 : 0);
        return (
          <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
            <span style={{ width: 150, fontSize: 12, color: 'var(--c-text-2)', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: w, maxWidth: '80%', height: 16, background: color, borderRadius: '0 4px 4px 0' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-text)' }}>{fmtNum(r.value)}</span>
              {r.sub && <span style={{ fontSize: 11, color: 'var(--c-text-3)' }}>{r.sub}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Analytics = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const wrapRef = useRef(null);
  const [chartW, setChartW] = useState(680);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await apiService.getAnalytics(d);
      if (!res.error) setData(res);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [load, days]);
  useEffect(() => {
    const measure = () => wrapRef.current && setChartW(Math.max(320, wrapRef.current.clientWidth - 40));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [data]);

  if (loading && !data) return <div style={{ padding: 80, textAlign: 'center' }}><Spinner size={26} /></div>;
  if (!data) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--c-text-2)' }}>Could not load analytics. <Btn small onClick={() => load(days)}>Retry</Btn></div>;

  const t = data.totals, f = data.funnel;
  const funnelRows = [
    { label: 'Contacted', value: f.contacted },
    { label: 'Replied', value: f.replied, sub: f.contacted ? `${(100 * f.replied / f.contacted).toFixed(1)}%` : '' },
    { label: 'Hot leads', value: f.hot, sub: f.replied ? `${(100 * f.hot / f.replied).toFixed(0)}% of replies` : '' },
    { label: 'Demos delivered', value: f.demos },
    { label: 'Won', value: f.won },
  ];
  const mailboxRows = (data.today_by_mailbox || []).map(([m, v]) => ({ label: m.split('@')[0], value: v }));

  return (
    <div className="viz-root" style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 20px 60px' }}>
      <style>{VIZ_CSS}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: 'var(--c-text)' }}>Analytics</div>
          <div style={{ fontSize: 13, color: 'var(--c-text-2)', marginTop: 3 }}>Outreach machine performance, all mailboxes combined</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '6px 13px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
              border: days === d ? '1px solid var(--c-border-bright)' : '1px solid var(--c-border)',
              background: days === d ? 'var(--c-primary-glow)' : 'transparent',
              color: days === d ? 'var(--c-primary)' : 'var(--c-text-2)',
            }}>{d}d</button>
          ))}
          <Btn small onClick={() => load(days)}><RefreshCw size={13} /></Btn>
        </div>
      </div>

      {/* Stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 16 }}>
        <StatCard icon={Send} label="Sent today" value={fmtNum(t.sent_today)} sub="campaign + follow-ups" />
        <StatCard icon={MessageSquare} label="Replies today" value={fmtNum(t.replies_today)} color="var(--c-purple)" />
        <StatCard icon={Percent} label={`Reply rate (${data.days}d)`} value={`${t.reply_rate}%`} sub={`${fmtNum(t.replies_win)} of ${fmtNum(t.sends_win)} sends`} color="var(--c-yellow)" />
        <StatCard icon={Database} label="Records" value={fmtNum(t.records)} sub={`${fmtNum(t.contacted)} contacted`} color="var(--c-blue)" />
        <StatCard icon={Layers} label="Mailable pool" value={fmtNum(t.mailable)} sub={`${fmtNum(t.enrichable)} more enrichable`} color="var(--c-accent)" />
      </div>

      {/* Daily sends */}
      <Card style={{ marginBottom: 14 }}>
        <div ref={wrapRef}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--c-text)' }}>Emails sent per day</div>
            <div>
              <LegendChip color="var(--s1)" label="campaign" />
              <LegendChip color="var(--s2)" label="follow-ups & replies sent" />
            </div>
          </div>
          <SendsChart series={data.series} width={chartW} />
          <details style={{ marginTop: 6 }}>
            <summary style={{ fontSize: 11.5, color: 'var(--c-text-3)', cursor: 'pointer' }}>table view</summary>
            <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 6 }}>
              <table style={{ fontSize: 11.5, color: 'var(--c-text-2)', borderCollapse: 'collapse' }}>
                <thead><tr>{['date', 'campaign', 'follow-ups', 'replies'].map((h) => <th key={h} style={{ textAlign: 'right', padding: '2px 10px', color: 'var(--c-text-3)' }}>{h}</th>)}</tr></thead>
                <tbody>{data.series.map((d) => (
                  <tr key={d.date}><td style={{ padding: '2px 10px' }}>{d.date}</td>
                    <td style={{ textAlign: 'right', padding: '2px 10px' }}>{d.campaign}</td>
                    <td style={{ textAlign: 'right', padding: '2px 10px' }}>{d.followups}</td>
                    <td style={{ textAlign: 'right', padding: '2px 10px' }}>{d.replies}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </details>
        </div>
      </Card>

      {/* Replies */}
      <Card style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--c-text)', marginBottom: 8 }}>Replies per day</div>
        <RepliesChart series={data.series} width={chartW} />
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>
        <Card>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--c-text)', marginBottom: 14 }}>Funnel (all time)</div>
          <BarList rows={funnelRows} />
          <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 6 }}>
            bounce rate last {data.days}d: {t.sends_win ? (100 * t.bounced_win / t.sends_win).toFixed(1) : 0}% ({fmtNum(t.bounced_win)})
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--c-text)', marginBottom: 14 }}>Sends today by mailbox</div>
          {mailboxRows.length ? <BarList rows={mailboxRows} /> :
            <div style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>No sends yet today.</div>}
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
